import React, { useEffect, useMemo, useState, useContext, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import qs from 'query-string'
import { ProjectCard } from '../../../components/2025/ProjectCard'
import shuffle from 'lodash.shuffle'
import { useQuery } from '@apollo/client'
import { DropdownFilter } from '../../../components/2025/DropdownFilter'
import { TextCenterDiv } from '../../../styles/2025/WorkStyles'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { QUERY } from '../../../graphql'
import { SpringContext } from '../../../context/SpringContext'
import { useViewport } from '../../../hook/useViewport'
import { ThemeContext } from '../../../context/2025/ThemeContext'
import { SearchContainer } from '../../../styles/2025/Styles'
// NOTE: Assuming useDebounce is available like in the previous component if needed
// import { useDebounce } from 'use-debounce';

const Header = React.lazy(() => import('../../../components/2025/Header'))
const Footer = React.lazy(() => import('../../../components/2025/Footer'))
const FullScreenLoader = React.lazy(() => import('../../../components/FullScreenLoader'))
const Pagination = React.lazy(() => import('../../../components/2025/Pagination'))

export default function Index(props) {
  const location = useLocation()
  const { search: searchParams } = location
  const { keyword, program, type, page } = qs.parse(searchParams) // Keep keyword if used
  const [gradsSearch, setGradsSearch] = useState('')
  // Optional: Debounce the search input if desired to reduce query frequency
  // const [gradsSearchDebounce] = useDebounce(gradsSearch, 500);
  const spring = useContext(SpringContext)
  const { width } = useViewport()
  const isMobile = width <= 640
  const { isDarkMode } = useContext(ThemeContext)
  const inputRef = useRef(null)
  // --- Step 1: Add ref to track user interaction ---
  const hasUserInteracted = useRef(false)

  const ITEMS_PER_PAGE = 12
  const history = useHistory()

  // useEffect(() => {
  //   if (spring != null && spring !== undefined) {
  //     spring.setCurrentValue(0).setAtRest()
  //     spring.setEndValue(0)
  //   }
  // }, [spring])

  const { data, loading } = useQuery(QUERY.PUBLIC_PROJECTS_QUERY, {
    variables: {
      where: {
        published: true,
        createdAt_gt: '2024-12-31',
        createdAt_lt: '2026-01-01',
      },
      // Use gradsSearch (or gradsSearchDebounce if using debounce)
      gradsSearch: gradsSearch,
    },
    // Optional: Reset interaction flag on error if needed
    // onError: () => { hasUserInteracted.current = false; }
  })

  const groupedProjects = useMemo(() => {
    if (!data?.projects) return []

    const filteredProjects = data.projects.filter((project) => {
      const programMatch =
        !program || project.members?.some((member) => member.user.program === program)

      const typeMatch =
        !type ||
        [
          project.primaryProjectType,
          project.secondaryProjectType,
          project.secondaryProjectType_2,
        ].some((t) => t?.toLowerCase() === type.toLowerCase())

      return programMatch && typeMatch
    })
    // Shuffle only if there are projects
    return filteredProjects.length > 0 ? shuffle(filteredProjects) : []
  }, [data, program, type])

  const currentPage = parseInt(page) || 1
  const totalPages = Math.ceil(groupedProjects.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedProjects = groupedProjects.slice(startIndex, endIndex)

  const handlePageChange = (newPage) => {
    const params = qs.parse(location.search)
    const newSearch = qs.stringify({ ...params, page: newPage })
    history.push(`${location.pathname}?${newSearch}`)

    if (currentPage !== newPage) {
      // --- Step 2: Set interaction flag on pagination ---
      // Focus might not be desired here, but set flag if loading might trigger
      // hasUserInteracted.current = true; // Uncomment if pagination causes loading and focus is desired
      if (spring) {
        spring.setCurrentValue(900).setAtRest()
        spring.setEndValue(900)
      }
    }
  }

  // Effect to reset page number when filters/search change
  useEffect(() => {
    const params = qs.parse(location.search)
    // Check if page is not 1 OR if it's 1 but there's only one page total
    const shouldResetPage = params.page && (params.page !== '1' || totalPages <= 1)

    if (shouldResetPage) {
      history.replace({
        pathname: location.pathname,
        search: qs.stringify({ ...params, page: 1 }),
      })
      // --- Step 2: Mark interaction when filters change causing page reset ---
      // This helps refocus if filter changes trigger loading state
      // hasUserInteracted.current = true; // Uncomment if filter changes cause loading and focus is desired
    }
    // Depend on totalPages as well to handle cases where results change page count
  }, [gradsSearch, program, type, totalPages, history, location.pathname]) // Added totalPages, history, pathname

  // --- Step 3: Modify the focus effect ---
  useEffect(() => {
    // Only focus if:
    // 1. Loading has finished (!loading)
    // 2. The input ref exists (inputRef.current)
    // 3. User interaction flag is true (hasUserInteracted.current)
    if (!loading && inputRef.current && hasUserInteracted.current) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          inputRef.current.focus({ preventScroll: true })
          // --- Step 4: Reset the flag after focusing ---
          hasUserInteracted.current = false
        })
      })
    } else if (!loading && !hasUserInteracted.current) {
      // Optional explicit blur
      // if (document.activeElement === inputRef.current) {
      //     inputRef.current.blur();
      // }
    }
  }, [loading]) // Only depends on loading

  const handleSearchChange = (e) => {
    const value = e.target.value
    setGradsSearch(value)
    // --- Step 2: Set interaction flag when user types ---
    // Set flag regardless of whether input is empty or not,
    // because even clearing might trigger a load/refocus cycle.
    // Resetting the flag happens *after* potential focus in the loading effect.
    hasUserInteracted.current = true
  }

  require('./work.scss')

  return (
    <div className='absolute w-full bg-black flex flex-col items-center justify-start pb-20 lg:pb-0'>
      {loading && (
        <FullScreenLoader
          color={isDarkMode ? `#DAFF01` : `#9167F0`}
          style={{ position: 'fixed' }}
        />
      )}
      {Header && <Header />}

      {/* Header Section */}
      <section
        className={`w-full ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-32 flex flex-col lg:flex-row items-center justify-center gap-32'>
          {/* ... (h1 and h3 content) ... */}
          <TextCenterDiv className=' px-[16px] lg:px-[48px]'>
            {' '}
            <h1
              className={`${isMobile ? 'heading-1-mobile' : 'heading-1'} ${
                isDarkMode ? 'text-lime' : 'text-lavender'
              } `}
              style={{ pointerEvents: 'none' }}
            >
              Work{' '}
            </h1>{' '}
            {isMobile ? (
              <div className='text-center pt-[16px]'>
                {' '}
                <h3
                  className={`heading-3b ${
                    isDarkMode ? 'text-white' : 'text-black'
                  } normal-case m-0`}
                  style={{ pointerEvents: 'none' }}
                >
                  YES! We’re bringing{' '}
                </h3>{' '}
                <h3
                  className='heading-3b text-white normal-case m-0'
                  style={{ pointerEvents: 'none' }}
                >
                  ideas to life!{' '}
                </h3>{' '}
              </div>
            ) : (
              <h3
                className={`heading-3b ${isDarkMode ? 'text-white' : 'text-black'} normal-case`}
                style={{ pointerEvents: 'none' }}
              >
                YES! We’re bringing ideas to life!{' '}
              </h3>
            )}{' '}
          </TextCenterDiv>
        </div>
      </section>

      {/* Filter Section */}
      <section
        className={`w-full ${isDarkMode ? 'bg-black' : 'bg-white'} transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] w-full mx-auto px-6 lg:px-[48px] py-32 flex flex-col lg:flex-row items-center justify-between gap-32'>
          <div className='w-full flex gap-6 px-[16px] lg:px-[48px]'>
            {/* Pass handleSearchChange to DropdownFilter if it contains the search input */}
            {/* Otherwise, keep the search input separate as shown below */}
            <DropdownFilter program={program} type={type} onChangeSearchGrads={setGradsSearch} />
          </div>
        </div>
      </section>

      {/* Count and Search Input Section */}
      <section
        className={`w-full ${
          isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
        } transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] flex flex-col lg:flex-row items-center justify-between gap-8 lg:gap-32 py-16'>
          {' '}
          {/* Adjusted padding */}
          <div
            className={`grid grid-cols-1 lg:grid-cols-2 w-full items-center ${
              isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
            } px-[16px] lg:px-[48px]`}
          >
            {/* Project Count Display */}
            <div>
              {' '}
              {/* Wrap count in a div for layout */}
              {!loading && groupedProjects?.length === 0 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  No projects found matching your criteria.
                </p>
              ) : !loading && groupedProjects?.length === 1 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  There is <span className={isDarkMode ? 'text-lime' : 'text-lavender'}>1</span>{' '}
                  project.
                </p>
              ) : !loading && groupedProjects?.length > 1 ? (
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  There are{' '}
                  <span className={isDarkMode ? 'text-lime' : 'text-lavender'}>
                    {groupedProjects?.length}
                  </span>{' '}
                  projects.
                </p>
              ) : loading ? (
                // Optional: Placeholder while loading count
                <p className={`heading-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
                  Loading projects...
                </p>
              ) : null}
            </div>

            {/* Search Input */}
            <SearchContainer
              className='flex relative w-full items-end justify-end' // Adjusted classes if needed
              hasError={false} // Example props, adjust as needed
              submitted={false}
              isDarkMode={isDarkMode}
            >
              <input
                type='text'
                id='gname'
                name='gname'
                ref={inputRef} // Keep ref attached
                placeholder='Search projects or graduates'
                className={`body-text placeholder-black w-full lg:w-4/5 ${
                  isDarkMode ? 'bg-white text-black' : 'bg-white text-black'
                } `} // Adjust styling as needed
                value={gradsSearch}
                onChange={handleSearchChange} // Use the new handler
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    // Optionally trigger something on Enter, e.g., set interaction flag again
                    // hasUserInteracted.current = true;
                  }
                }}
                // Ensure no autoFocus={true} prop
              />
              {/* Search Icon SVG */}
              <svg
                width='26'
                height='26'
                viewBox='0 0 26 26'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={`absolute pointer-events-none ${isDarkMode ? 'right-2' : 'right-2'}`} // Adjust positioning
              >
                <g id='Frame 76'>
                  <path
                    id='Vector'
                    d='M0.93934 22.9393C0.353553 23.5251 0.353553 24.4749 0.93934 25.0607C1.52513 25.6464 2.47487 25.6464 3.06066 25.0607L0.93934 22.9393ZM22.5 9.7C22.5 13.1242 19.7242 15.9 16.3 15.9V18.9C21.381 18.9 25.5 14.781 25.5 9.7H22.5ZM16.3 15.9C12.8758 15.9 10.1 13.1242 10.1 9.7H7.1C7.1 14.781 11.219 18.9 16.3 18.9V15.9ZM10.1 9.7C10.1 6.27584 12.8758 3.5 16.3 3.5V0.5C11.219 0.5 7.1 4.61898 7.1 9.7H10.1ZM16.3 3.5C19.7242 3.5 22.5 6.27583 22.5 9.7H25.5C25.5 4.61898 21.381 0.5 16.3 0.5V3.5ZM3.06066 25.0607L7.56066 20.5607L5.43934 18.4393L0.93934 22.9393L3.06066 25.0607ZM7.56066 20.5607L12.0607 16.0607L9.93934 13.9393L5.43934 18.4393L7.56066 20.5607Z'
                    fill={isDarkMode ? '#FFFFFF' : '#000000'} // Adjust icon color for dark/light mode
                  />
                </g>
              </svg>
            </SearchContainer>
          </div>
        </div>
      </section>

      {/* Project Grid Section */}
      <section
        className={`w-full ${
          isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'
        } transition-colors duration-300 `}
      >
        <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 flex flex-col items-center justify-between gap-16'>
          {' '}
          {/* Adjusted padding/gap */}
          <TransitionGroup
            className={`grid grid-cols-1 lg:grid-cols-3 gap-16 w-full ${
              /* Removed padding here, handled by parent */ ''
            } ${isDarkMode ? 'bg-[#222222]' : 'bg-grey-25'}`}
          >
            {/* Render only if not loading and projects exist */}
            {!loading &&
              paginatedProjects?.length > 0 &&
              paginatedProjects.map((project) => (
                <CSSTransition key={project.id} timeout={500} classNames='item'>
                  {/* Ensure ProjectCard receives isDarkMode if needed */}
                  <ProjectCard project={project} isDarkMode={isDarkMode} />
                </CSSTransition>
              ))}
          </TransitionGroup>
        </div>
      </section>

      {/* Pagination Section */}
      {totalPages > 1 && (
        <section
          className={`w-full ${
            isDarkMode ? 'bg-[#222222]' : 'bg-light-grey-25' // Check background color consistency
          } transition-colors duration-300 `}
        >
          <div className='max-w-[1440px] mx-auto px-6 lg:px-[48px] py-16 flex flex-col items-center justify-between gap-16'>
            {' '}
            {/* Adjusted padding/gap */}
            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              itemsPerPage={ITEMS_PER_PAGE}
              onPageChange={handlePageChange}
              // Pass isDarkMode if Pagination component needs it
              // isDarkMode={isDarkMode}
            />
          </div>
        </section>
      )}

      {/* Footer Section */}
      <div
        className={`relative w-full flex flex-col items-center justify-center w-full ${
          isDarkMode ? 'bg-black' : 'bg-light-grey-25' // Check background consistency
        }`}
      >
        <div
          className={`relative bottom-0 w-full pt-16 px-[16px] lg:px-[48px] bg-black max-w-[1440px] ${
            isDarkMode ? 'bg-black' : 'bg-light-grey-25' // Check background consistency
          }`}
        >
          {Footer && <Footer />}
        </div>
      </div>
    </div>
  )
}
